import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';
import { formatCurrencyShort } from '@/utils/currency';

export default function AppreciationChart({ initialValue, currentValue, projectedValue, stages }) {
  const maxValue = projectedValue * 1.1;
  const getYPosition = (value) => {
    return 100 - ((value / maxValue) * 80);
  };

  const dataPoints = [
    { label: 'Initial', value: initialValue, x: 10 },
    { label: 'Current', value: currentValue, x: 50 },
    { label: 'Projected', value: projectedValue, x: 90 }
  ];

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-8" data-testid="appreciation-chart">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-xl font-medium">Property Appreciation</h3>
        <div className="flex items-center space-x-2 text-[#4ADE80]">
          <TrendingUp className="w-5 h-5" />
          <span className="font-mono text-lg">
            +{(((projectedValue - initialValue) / initialValue) * 100).toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="relative h-64 mb-8">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map((y) => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="0.5"
            />
          ))}

          {/* Appreciation curve */}
          <motion.path
            d={`M ${dataPoints[0].x} ${getYPosition(dataPoints[0].value)} 
                Q ${dataPoints[1].x - 10} ${getYPosition(dataPoints[1].value)}, ${dataPoints[1].x} ${getYPosition(dataPoints[1].value)}
                Q ${dataPoints[2].x - 20} ${getYPosition(dataPoints[2].value) - 5}, ${dataPoints[2].x} ${getYPosition(dataPoints[2].value)}`}
            fill="none"
            stroke="#4ADE80"
            strokeWidth="0.8"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          />

          {/* Gradient fill under curve */}
          <defs>
            <linearGradient id="appreciationGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#4ADE80" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#4ADE80" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            d={`M ${dataPoints[0].x} ${getYPosition(dataPoints[0].value)} 
                Q ${dataPoints[1].x - 10} ${getYPosition(dataPoints[1].value)}, ${dataPoints[1].x} ${getYPosition(dataPoints[1].value)}
                Q ${dataPoints[2].x - 20} ${getYPosition(dataPoints[2].value) - 5}, ${dataPoints[2].x} ${getYPosition(dataPoints[2].value)}
                L ${dataPoints[2].x} 100 L ${dataPoints[0].x} 100 Z`}
            fill="url(#appreciationGradient)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
          />

          {/* Data points */}
          {dataPoints.map((point, index) => (
            <motion.g
              key={index}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.3 + 0.5 }}
            >
              <circle
                cx={point.x}
                cy={getYPosition(point.value)}
                r="2"
                fill="#4ADE80"
              />
              <circle
                cx={point.x}
                cy={getYPosition(point.value)}
                r="1.2"
                fill="#000"
              />
            </motion.g>
          ))}
        </svg>

        {/* Labels */}
        <div className="absolute inset-0 pointer-events-none">
          {dataPoints.map((point, index) => (
            <motion.div
              key={index}
              className="absolute"
              style={{ left: `${point.x}%`, top: `${getYPosition(point.value)}%` }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.3 + 1 }}
            >
              <div className="transform -translate-x-1/2 -translate-y-full mb-2">
                <div className="bg-[#111111] border border-white/10 rounded-lg px-3 py-2 text-center">
                  <p className="text-xs text-white/40 mb-1">{point.label}</p>
                  <p className="text-sm font-mono text-white">{formatCurrencyShort(point.value)}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-xs text-white/40 mb-1">Construction Start</p>
          <p className="text-base font-mono">{formatCurrencyShort(initialValue)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-white/40 mb-1">Current Value</p>
          <p className="text-base font-mono text-[#4ADE80]">{formatCurrencyShort(currentValue)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-white/40 mb-1">Est. at Completion</p>
          <p className="text-base font-mono">{formatCurrencyShort(projectedValue)}</p>
        </div>
      </div>
    </div>
  );
}